/**
 * Lightweight pub/sub for "VitalEngine cache changed" notifications.
 *
 * Anyone who mutates the local cache (data refresh, facility refresh) calls
 * <code>notifyCacheRefreshed()</code>. Pages whose data depends on the cache —
 * e.g. the Home dashboard customer cards — subscribe via
 * <code>subscribeCacheRefreshed</code> and refetch when notified.
 *
 * The signal is broadcast two ways:
 *  - A <code>CustomEvent</code> on <code>window</code> for same-tab listeners.
 *  - A <code>localStorage</code> write for cross-tab listeners (the browser
 *    fires a <code>storage</code> event in every other tab on the same origin).
 *
 * Payload carries the kind ("data" | "facilities" | "customers") and a
 * timestamp; consumers usually just need to know that something changed.
 */

const EVENT_NAME = 'vitaldata:cache-refreshed';
const STORAGE_KEY = 'vitaldata:cache-refreshed';

/** Call after admin creates/updates/deletes a customer or assignments. */
export function notifyCustomersChanged() {
  notifyCacheRefreshed('customers');
}

export function notifyCacheRefreshed(kind = 'data') {
  const detail = { kind, at: Date.now() };

  if (typeof window !== 'undefined') {
    try {
      window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail }));
    } catch {
      // Older browsers without CustomEvent support are out of scope here.
    }

    try {
      // The 'storage' event fires only in OTHER tabs, never the writer's own,
      // which is exactly what we want — same-tab is covered by the CustomEvent
      // dispatched above.
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(detail));
    } catch {
      // Private mode / disabled storage — cross-tab sync just won't fire.
    }
  }
}

/**
 * Subscribe to cache-refresh notifications. Returns an unsubscribe function
 * suitable for returning straight from a useEffect cleanup.
 */
export function subscribeCacheRefreshed(handler) {
  if (typeof window === 'undefined') return () => {};

  const onCustom = (e) => handler(e.detail);
  const onStorage = (e) => {
    if (e.key !== STORAGE_KEY || !e.newValue) return;
    try {
      handler(JSON.parse(e.newValue));
    } catch {
      handler({ kind: 'data', at: Date.now() });
    }
  };

  window.addEventListener(EVENT_NAME, onCustom);
  window.addEventListener('storage', onStorage);

  return () => {
    window.removeEventListener(EVENT_NAME, onCustom);
    window.removeEventListener('storage', onStorage);
  };
}
